import LoginSkeleton from 'components/LoginSkeleton';
import { useAPI } from 'hooks/useAPI';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { hardSignout, selectToken, signout, updateTokenAfterReauth } from 'slices/auth';
import { hideReauthModal, reauthSuccess, selectIframeReady, setIframeReady } from 'slices/reauth';
import './reauthModal.css';

const ReauthModal = ({ show, isInitialAuth = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { AuthAPI, BlobsAPI } = useAPI();
  const iframeReady = useSelector(selectIframeReady);
  const currentToken = useSelector(selectToken);

  // Cross-origin subdomain mode when a real login origin is configured (e.g. https://auth.example);
  // otherwise the login is served from the same (single) static-host origin and loaded in an
  // opaque-origin sandbox iframe. Build-time constant values (read from the env each render, so
  // they're stable primitives that never actually change).
  const authBaseUrl = import.meta.env.VITE_AUTH_BASE_URL;
  const crossOrigin = /^https?:\/\//.test(authBaseUrl || '');
  const authSrc = crossOrigin ? `${authBaseUrl}/auth.html` : 'auth.html';

  // Sticky "the iframe has been shown" latch. `iframeReady` flips back to false on reauthSuccess
  // (it dismisses the reauth prompt), which would otherwise fade the login iframe out the instant
  // login succeeds — exposing the bare skeleton (no spinner) before the app is ready. Once shown,
  // keep the real login screen (with its own loading overlay) visible until this modal unmounts
  // (for the initial auth that is when the first app page has mounted; see Shell + App <Ready>).
  const [iframeShown, setIframeShown] = useState(false);
  useEffect(() => {
    if (iframeReady) setIframeShown(true);
  }, [iframeReady]);

  const iframeRef = useRef(null);
  const timeoutIdRef = useRef(null);
  const portRef = useRef(null);

  // Keep the latest current token in a ref so the channel-setup effect below need not depend on it
  // (which would tear down and rebuild the port whenever the token changes); the port handler reads
  // the ref when it replies.
  const currentTokenRef = useRef(currentToken);
  useEffect(() => {
    currentTokenRef.current = currentToken;
  }, [currentToken]);

  // Close the private port when the modal unmounts (its frame is being torn down anyway).
  useEffect(
    () => () => {
      portRef.current?.close?.();
      portRef.current = null;
    },
    []
  );

  // Initial auth: the login iframe is created early in index.html (#auth-frame) so it loads in
  // parallel with the main bundle. Adopt that exact element *in place* — it is already a fixed
  // full-screen overlay, so we never move it (reparenting an iframe reloads it, throwing away the
  // already-loaded login). We just take a ref to it here and, on teardown (after appReady, when
  // Shell unmounts us), remove it so the cross-origin auth context is unloaded. Deps are
  // isInitialAuth plus the build-constant auth src/mode — all stable, so this still runs once per
  // mount and the cleanup must not fire (and tear down a live iframe) on incidental re-renders.
  useEffect(() => {
    if (!isInitialAuth) return undefined;
    let existing = document.getElementById('auth-frame');
    if (!existing) {
      // Fallback: index.html's inline script normally pre-creates this (so it loads in parallel
      // with the main bundle). If it didn't (e.g. dev/HMR, or index.html changed), create it here
      // so login still works — React's own message handler below will reveal it on DOMContentLoaded.
      existing = document.createElement('iframe');
      existing.id = 'auth-frame';
      existing.src = authSrc;
      // Same-origin build: run the login in an opaque-origin sandbox so the app can't read its DOM
      // or keystrokes (raw code/picture). No allow-same-origin — that would defeat the boundary.
      if (!crossOrigin) existing.setAttribute('sandbox', 'allow-scripts');
      existing.setAttribute(
        'style',
        'position:fixed;top:0;left:0;right:0;bottom:0;width:100%;height:100%;border:0;background:transparent;opacity:0;transition:opacity 250ms;z-index:10002;'
      );
      document.body.appendChild(existing);
    }
    iframeRef.current = existing;
    existing.classList.add('reauthModalIframe');
    return () => {
      document.getElementById('auth-frame')?.remove();
      iframeRef.current = null;
    };
  }, [isInitialAuth, authSrc, crossOrigin]);

  // Localize the adopted iframe's title separately (index.html seeds a plain "Login"); kept out of
  // the adoption effect so a changing `t` identity can never trigger that effect's teardown.
  useEffect(() => {
    if (!isInitialAuth) return;
    const el = document.getElementById('auth-frame');
    if (el) el.title = t('reauth.iframe_title');
  }, [isInitialAuth, t]);

  // Keep the adopted iframe's reveal in sync with React state (the inline script in index.html
  // already reveals it on the iframe's DOMContentLoaded; this is the belt-and-suspenders path and
  // never hides it back).
  useEffect(() => {
    if (!isInitialAuth || !iframeShown) return;
    const el = iframeRef.current || document.getElementById('auth-frame');
    if (el) el.style.opacity = '1';
  }, [isInitialAuth, iframeShown]);

  useEffect(() => {
    if (!show) return;

    // Only set timeout for reauth, not for initial auth
    if (!isInitialAuth) {
      timeoutIdRef.current = setTimeout(
        () => {
          dispatch(signout(AuthAPI));
          navigate('/', { replace: true });
          dispatch(hideReauthModal());
        },
        (import.meta.env.VITE_REAUTH_MODAL_TIMEOUT || 30) * 1000
      );
    }

    // Messages that ride the private port handed to the login frame: the token handoff and the
    // reauth outcome. A port is point-to-point (no origin/targetOrigin), so no other embedded frame
    // can read or forge these.
    const handlePortMessage = (event) => {
      const data = event.data;
      if (data?.type === 'requestCurrentToken') {
        portRef.current?.postMessage({
          type: 'currentToken',
          token: currentTokenRef.current || null,
        });
      } else if (data?.type === 'ready') {
        dispatch(setIframeReady());
      } else if (data?.type === 'reauthSuccess' && data?.token && data?.user) {
        if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
        dispatch(
          updateTokenAfterReauth({
            token: data.token,
            user: data.user,
            encryptionKey: data.encryptionKey,
            navigationState: data.navigationState,
          })
        );
        dispatch(reauthSuccess());
      } else if (data?.type === 'panic') {
        if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
        dispatch(hardSignout([AuthAPI, BlobsAPI, { panic: true }]));
        navigate('/', { replace: true });
      }
    };

    // Window-level bootstrap from the login frame — only non-secret signals. `DOMContentLoaded`
    // reveals the frame (initial auth); `hello` triggers the one-time port handoff. Every message is
    // pinned to the exact frame we created (event.source), which is what lets the single-origin
    // build trust it: there an origin check is meaningless (every bucket shares the one origin).
    const handleWindowMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const type = event.data?.type;
      if (type === 'DOMContentLoaded') {
        if (isInitialAuth) dispatch(setIframeReady());
      } else if (type === 'hello') {
        if (portRef.current) return; // channel already established for this frame
        const channel = new MessageChannel();
        portRef.current = channel.port1;
        channel.port1.onmessage = handlePortMessage;
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'port' },
          crossOrigin ? authBaseUrl : '*',
          [channel.port2]
        );
      }
    };

    window.addEventListener('message', handleWindowMessage);

    // Replay any bootstrap messages the index.html inline buffer captured before React mounted
    // (initial auth only), then retire that early listener so only this handler runs. Drained once:
    // on later re-runs of this effect the queue is already null.
    if (isInitialAuth && window.__authMsgQueue) {
      const queued = window.__authMsgQueue;
      window.__authMsgQueue = null;
      if (window.__authMsgListener) {
        window.removeEventListener('message', window.__authMsgListener);
        window.__authMsgListener = null;
      }
      queued.forEach((event) => {
        handleWindowMessage(event);
      });
    }

    return () => {
      window.removeEventListener('message', handleWindowMessage);
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    };
  }, [show, isInitialAuth, dispatch, navigate, AuthAPI, BlobsAPI, crossOrigin]);

  // Keep modal iframe forcefully focused when it's ready
  useEffect(() => {
    const iframe = iframeRef.current;

    if (!show || !iframeReady || !iframe) return;

    const handleBlur = () => {
      // RAF ensures the blur event finishes before we attempt to refocus
      requestAnimationFrame(() => {
        if (document.activeElement !== iframe) {
          iframe.focus();
        }
      });
    };

    iframe.addEventListener('blur', handleBlur);
    iframe.focus();

    return () => iframe.removeEventListener('blur', handleBlur);
  }, [show, iframeReady]);

  if (!show) return null;

  return (
    <div
      className="reauthModal"
      data-testid="login-screen"
      style={{
        position: 'fixed',
        opacity: isInitialAuth ? 1 : iframeReady ? 0.95 : 0,
        pointerEvents: iframeReady ? 'auto' : 'none',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: isInitialAuth || iframeReady ? 10001 : -1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isInitialAuth
          ? 'var(--tg-theme-secondary-bg-color)'
          : 'rgba(0, 0, 0, 0.5)',
        transition: 'opacity 250ms',
      }}
    >
      {isInitialAuth ? (
        // Initial auth: the iframe is the adopted #auth-frame (created early in index.html as a
        // fixed overlay above this skeleton). We render only the skeleton here — it shows until the
        // adopted iframe paints. Same component as the shell's post-login cover, for a seamless
        // login → app transition.
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }}>
          <LoginSkeleton />
        </div>
      ) : (
        // Reauth: created fresh by React (no pre-warmed iframe exists mid-session).
        <iframe
          ref={iframeRef}
          src={authSrc}
          sandbox={crossOrigin ? undefined : 'allow-scripts'}
          className="reauthModalIframe"
          title={t('reauth.iframe_title')}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            background: 'transparent',
            opacity: iframeShown ? 1 : 0,
            transition: 'opacity 250ms',
            zIndex: 1,
          }}
        />
      )}
    </div>
  );
};

export default ReauthModal;
